import { cloneElement, createElement, isValidElement, useId } from "react";
import { Link } from "react-router-dom";
import { MdInfoOutline } from "react-icons/md";
import Card from "components/card";
import Skeleton from "components/skeleton/Skeleton";
import Tooltip from "components/tooltip";
import TrendIndicator from "components/stat-card/TrendIndicator";

const ACCENTS = {
  sage: "bg-brand-100 text-brand-800 dark:bg-brand-900/40 dark:text-brand-200",
  sky: "bg-sky-100 text-sky-800 dark:bg-sky-900/40 dark:text-sky-200",
  slate: "bg-slate-100 text-slate-700 dark:bg-slate-700/40 dark:text-slate-200",
  gold: "bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-200",
  danger: "bg-danger-subtle text-danger-fg",
  neutral: "bg-surface-subtle text-fg-muted",
};

const VALUE_SIZES = { md: "text-2xl", lg: "text-3xl" };

const renderIcon = (icon) => {
  const sizing = "h-5 w-5";
  if (isValidElement(icon)) {
    return cloneElement(icon, {
      "aria-hidden": true,
      className: `${sizing} ${icon.props.className ?? ""}`.trim(),
    });
  }
  return createElement(icon, { "aria-hidden": true, className: sizing });
};

/**
 * Metric tile: accent icon box, label, value, optional trend pill and caption.
 * With `onClick` or `href` the whole card is one button/link (stretched hit area);
 * the `info` tooltip trigger stays separately focusable.
 *
 * @param {object} props
 * @param {React.ElementType|React.ReactElement} [props.icon] e.g. `MdPeople` or `<MdPeople />`.
 * @param {"sage"|"sky"|"slate"|"gold"|"danger"|"neutral"} [props.accent="sage"] Icon box tint.
 * @param {React.ReactNode} props.label
 * @param {React.ReactNode} props.value Pre-formatted value (see `utils/format`).
 * @param {{value: number, invert?: boolean, suffix?: string}} [props.trend] See TrendIndicator.
 * @param {React.ReactNode} [props.caption] Small text under the value, e.g. "vs last month".
 * @param {React.ReactNode} [props.info] Tooltip content behind an info icon next to the label.
 * @param {boolean} [props.loading=false] Skeletons in place of value/trend/caption.
 * @param {() => void} [props.onClick] Renders the card as a button.
 * @param {string} [props.href] In-app route; renders the card as a router Link.
 * @param {"md"|"lg"} [props.size="md"] Value text-2xl / text-3xl.
 * @param {string} [props.className] Extra Card classes (grid placement etc.).
 */
const StatCard = ({
  icon,
  accent = "sage",
  label,
  value,
  trend,
  caption,
  info,
  loading = false,
  onClick,
  href,
  size = "md",
  className = "",
}) => {
  const baseId = useId();
  const labelId = `${baseId}-label`;
  const valueId = `${baseId}-value`;
  const trendId = `${baseId}-trend`;
  const captionId = `${baseId}-caption`;

  const interactive = Boolean(onClick || href);
  const hasTrend = !loading && trend && trend.value !== null && trend.value !== undefined;
  const labelledBy = [labelId, valueId, hasTrend && trendId, caption && captionId]
    .filter(Boolean)
    .join(" ");

  const hitAreaClass = "focus-ring absolute inset-0 rounded-card";
  let hitArea = null;
  if (href) {
    hitArea = <Link to={href} className={hitAreaClass} aria-labelledby={labelledBy} />;
  } else if (onClick) {
    hitArea = (
      <button type="button" onClick={onClick} className={hitAreaClass} aria-labelledby={labelledBy} />
    );
  }

  return (
    <Card
      padding="md"
      aria-busy={loading || undefined}
      extra={`${interactive ? "cursor-pointer transition-colors hover:bg-surface-raised" : ""} ${className}`.trim()}
    >
      {hitArea}
      <div className={`flex items-start gap-4 ${interactive ? "pointer-events-none relative" : ""}`.trim()}>
        {icon && (
          <span
            className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-control ${
              ACCENTS[accent] ?? ACCENTS.sage
            }`}
          >
            {renderIcon(icon)}
          </span>
        )}
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-1">
            <p id={labelId} className="truncate text-sm font-medium text-fg-muted">
              {label}
            </p>
            {info && (
              <Tooltip
                placement="top"
                content={info}
                trigger={
                  <button
                    type="button"
                    aria-label={typeof label === "string" ? `About ${label}` : "More info"}
                    className="focus-ring pointer-events-auto relative shrink-0 rounded-full text-fg-muted transition-colors hover:text-fg"
                  >
                    <MdInfoOutline aria-hidden="true" className="h-4 w-4" />
                  </button>
                }
              />
            )}
          </div>

          {loading ? (
            <div className="mt-2 flex flex-col gap-2">
              <Skeleton className={`${size === "lg" ? "h-9" : "h-8"} w-24`} />
              {caption && <Skeleton className="h-3 w-32" />}
            </div>
          ) : (
            <>
              <div className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-1">
                <p
                  id={valueId}
                  className={`${VALUE_SIZES[size] ?? VALUE_SIZES.md} font-bold leading-tight text-fg`}
                >
                  {value}
                </p>
                {hasTrend && <TrendIndicator id={trendId} {...trend} />}
              </div>
              {caption && (
                <p id={captionId} className="mt-1 text-xs text-fg-muted">
                  {caption}
                </p>
              )}
            </>
          )}
        </div>
      </div>
    </Card>
  );
};

export default StatCard;
